import React from 'react';
import { Button, EmptyState, ModalBase, SkeletonList, Table, Tooltip } from '@bynder/design-system';
import { IconDelete, IconUserGroup } from '@bynder/icons';
import { Translate, useTranslate } from '@bynder/localization';
import { Group, Page } from './types';

interface Props {
	groups: Group[];
	editGroup: (group: Group) => void;
	onRemove: (event: React.MouseEvent<HTMLElement, MouseEvent>, group: Group) => void;
	setModalPage: (page: Page) => void;
	closeModal: () => void;
	isLoading: boolean;
}

export function GroupsList({
	groups,
	editGroup,
	onRemove,
	setModalPage,
	closeModal,
	isLoading,
}: Props) {
	const { translate } = useTranslate();

	if (isLoading) {
		return (
			<>
				<ModalBase.Header title={translate('workflow.ManageGroups')} />
				<ModalBase.Content>
					<SkeletonList itemRender={<SkeletonList.Item />} />
				</ModalBase.Content>
				<ModalBase.Footer
					actionPrimary={
						<Button
							aria-label={translate('workflow.AddGroup')}
							variant="primary"
							onClick={() => setModalPage('add')}
							data-testid="wf-group-management-add-group"
						>
							<Translate id="workflow.AddGroup" />
						</Button>
					}
					actionSecondary={
						<Button
							aria-label={translate('workflow.Close')}
							variant="secondary"
							onClick={closeModal}
							data-testid="wf-group-management-close"
						>
							<Translate id="workflow.Close" />
						</Button>
					}
				/>
			</>
		);
	}

	return (
		<>
			<ModalBase.Header title={translate('workflow.ManageGroups')} />
			<ModalBase.Content>
				{groups.length === 0 ? (
					<EmptyState
						icon={<IconUserGroup />}
						title={translate('workflow.NoGroup')}
						text={translate('workflow.StartAddGroup')}
					/>
				) : (
					<Table>
						<Table.Head>
							<Table.HeadCell>
								<Translate id="workflow.Name" />
							</Table.HeadCell>
							<Table.HeadCell>
								<Translate id="workflow.Users" />
							</Table.HeadCell>
							<Table.HeadCell />
						</Table.Head>
						<Table.Body>
							{groups.map((group) => (
								<Table.Row
									key={group.id}
									data-testid="wf-group-management-group-item"
									onClick={() => editGroup(group)}
								>
									<Table.Cell>{group.name}</Table.Cell>
									<Table.Cell>{group.amountUsers}</Table.Cell>
									<Table.Cell>
										<Tooltip content={translate('confirm.RemoveGroup')}>
											<Button
												aria-label="remove-group"
												data-testid="wf-group-management-group-remove"
												variant="clean"
												onClick={(event) => onRemove(event, group)}
											>
												<IconDelete />
											</Button>
										</Tooltip>
									</Table.Cell>
								</Table.Row>
							))}
						</Table.Body>
					</Table>
				)}
			</ModalBase.Content>
			<ModalBase.Footer
				actionPrimary={
					<Button
						aria-label="add-group"
						variant="primary"
						onClick={() => setModalPage('add')}
						data-testid="wf-group-management-add-group"
					>
						<Translate id="workflow.AddGroup" />
					</Button>
				}
				actionSecondary={
					<Button
						aria-label="close"
						variant="secondary"
						onClick={closeModal}
						data-testid="wf-group-management-close"
					>
						<Translate id="workflow.Close" />
					</Button>
				}
			/>
		</>
	);
}
